import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { apiFetch } from "../../utils/api";
import { loadRazorpay, fmtINR } from "../../utils/payment";
import Header from "../../components/layout/Header";
import { Button } from "../../components/common/Common";
import {
  ArrowLeft, Check, ShieldCheck, Clock, AlertTriangle, RefreshCw, Zap,
} from "lucide-react";
import "./Billing.css";

const CYCLE_HINT = {
  monthly: "Billed every month",
  quarterly: "Billed every 3 months · 10% off",
  yearly: "Billed yearly · 2 months free",
};

const Billing = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { showToast } = useToast();
  const { status, refresh, blocked, trial, daysLeft, hasAutopay } = useSubscription();

  const [plans, setPlans] = useState([]);
  const [billable, setBillable] = useState(status?.billable || 1);
  const [selected, setSelected] = useState("monthly");
  const [charges, setCharges] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadPlans = useCallback(async () => {
    try {
      const res = await apiFetch("subscription/plans");
      if (res?.success && res.data?.plans) {
        setPlans(res.data.plans);
        setBillable(res.data.billable || 1);
      }
    } catch { showToast("Could not load plans", "error"); }
  }, [showToast]);

  const loadCharges = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await apiFetch("subscription/charges");
      if (res?.success && res.data?.charges) setCharges(res.data.charges);
    } catch { /* history is best-effort */ }
  }, [isAdmin]);

  useEffect(() => { loadPlans(); loadCharges(); }, [loadPlans, loadCharges]);

  const selectedPlan = plans.find((p) => p.cycle === selected);

  // Create the subscription server-side, then open Razorpay to authorise autopay.
  const setupAutopay = async () => {
    if (!selectedPlan) return;
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { showToast("Payment gateway failed to load.", "error"); setBusy(false); return; }

      const res = await apiFetch("subscription/setup", {
        method: "POST",
        body: JSON.stringify({ cycle: selected }),
      });
      if (!res?.success || !res.data?.subscription_id) {
        showToast(res?.message || "Could not start autopay setup", "error");
        setBusy(false);
        return;
      }
      const { key_id, subscription_id } = res.data;

      const rzp = new window.Razorpay({
        key: key_id,
        subscription_id,
        name: "RABS Connect",
        description: `Autopay · ${selectedPlan.label} · ${billable} seat${billable > 1 ? "s" : ""}`,
        theme: { color: "#d4a819" },
        handler: async (resp) => {
          const vr = await apiFetch("subscription/verify", {
            method: "POST",
            body: JSON.stringify({
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            }),
          });
          if (vr?.success) {
            showToast("Autopay activated!", "success");
            try { localStorage.removeItem("subscription_expired"); } catch { }
            await refresh();
            loadCharges();
          } else {
            showToast(vr?.message || "Could not confirm autopay. It may still activate shortly.", "info");
            await refresh();
          }
        },
        modal: { ondismiss: () => showToast("Autopay setup cancelled.", "info") },
      });
      rzp.on("payment.failed", () => showToast("Authorisation failed. Please try again.", "error"));
      rzp.open();
    } catch {
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setBusy(false);
    }
  };

  const cancelAutopay = async () => {
    if (!window.confirm("Stop autopay at the end of the current cycle?")) return;
    setBusy(true);
    try {
      const res = await apiFetch("subscription/cancel", { method: "POST" });
      showToast(res?.message || (res?.success ? "Autopay will stop at cycle end." : "Could not cancel"), res?.success ? "success" : "error");
      await refresh();
    } catch { showToast("Could not cancel autopay", "error"); }
    setBusy(false);
  };

  // ── Status banner tone ──
  const tone = blocked ? "danger" : trial ? "trial" : "ok";
  const stateTitle = blocked
    ? "Subscription inactive"
    : trial
      ? `Free trial — ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`
      : "Autopay active";
  const stateSub = blocked
    ? (isAdmin ? "Set up autopay below to restore access for your team." : "Please ask your master to renew the subscription.")
    : trial
      ? "Enjoy full access. Set up autopay now so billing continues seamlessly after the trial."
      : `Renews automatically · ${status?.quantity ?? billable} billable seat${(status?.quantity ?? billable) > 1 ? "s" : ""}.`;

  return (
    <div>
      <Header
        title="Billing & Subscription"
        subtitle="Manage your plan and autopay"
        actions={<Button variant="outline" icon={ArrowLeft} onClick={() => navigate("/")}>Back</Button>}
      />
      <div className="page bill">
        {/* Status card */}
        <div className={`bill-status bill-status--${tone}`}>
          <div className="bill-status__icon">
            {blocked ? <AlertTriangle size={22} /> : trial ? <Clock size={22} /> : <ShieldCheck size={22} />}
          </div>
          <div className="bill-status__body">
            <h2 className="bill-status__title">{stateTitle}</h2>
            <p className="bill-status__sub">{stateSub}</p>
          </div>
          {hasAutopay && isAdmin && !blocked && (
            <button className="bill-status__cancel" onClick={cancelAutopay} disabled={busy}>
              Cancel autopay
            </button>
          )}
        </div>

        <div className="bill-note">
          <Zap size={14} /> ₹800 per user / month. Your master is free while you have at least one paid user.
        </div>

        {/* Admin: choose a plan + set up autopay */}
        {isAdmin ? (
          <>
            <h3 className="bill-section-title">Choose a billing cycle</h3>
            <div className="bill-plans">
              {plans.map((p) => (
                <button
                  key={p.cycle}
                  className={`bill-plan ${selected === p.cycle ? "bill-plan--active" : ""}`}
                  onClick={() => setSelected(p.cycle)}
                >
                  {selected === p.cycle && <span className="bill-plan__tick"><Check size={12} /></span>}
                  <span className="bill-plan__name">{p.label}</span>
                  <span className="bill-plan__price">{fmtINR(p.per_user_amount)}<small>/user</small></span>
                  <span className="bill-plan__hint">{CYCLE_HINT[p.cycle]}</span>
                  <span className="bill-plan__total">
                    Total {fmtINR(p.total_amount)} <small>× {billable} seat{billable > 1 ? "s" : ""}</small>
                  </span>
                </button>
              ))}
            </div>

            <div className="bill-cta">
              <div className="bill-cta__summary">
                <span>{selectedPlan?.label || "—"} · {billable} seat{billable > 1 ? "s" : ""}</span>
                <strong>{selectedPlan ? fmtINR(selectedPlan.total_amount) : "—"}</strong>
              </div>
              <Button variant="gold" icon={hasAutopay ? RefreshCw : ShieldCheck} onClick={setupAutopay} disabled={busy || !selectedPlan}>
                {busy ? "Processing…" : hasAutopay ? "Change / re-authorise autopay" : "Set up autopay"}
              </Button>
            </div>

            {/* Billing history */}
            {charges.length > 0 && (
              <>
                <h3 className="bill-section-title">Billing history</h3>
                <div className="bill-history">
                  <div className="bill-history__row bill-history__row--head">
                    <span>Date</span><span>Cycle</span><span>Seats</span><span>Amount</span><span>Status</span>
                  </div>
                  {charges.map((c) => (
                    <div key={c.id} className="bill-history__row">
                      <span>{String(c.created_at || "").split(" ")[0]}</span>
                      <span>{c.cycle || "—"}</span>
                      <span>{c.quantity ?? "—"}</span>
                      <span>{fmtINR(c.amount)}</span>
                      <span className={`bill-badge bill-badge--${c.status}`}>{c.status}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="bill-readonly">
            <ShieldCheck size={18} />
            Billing is managed by your master. Please contact them to change the plan or set up autopay.
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;
